import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import Toast from './components/Notifications/Toast';
import './App.css';

// Components
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import Layout from './components/Layout/Layout';

// User Management
import UserList from './components/Users/UserList';
import UserForm from './components/Users/UserForm';

// Suppliers
import SupplierList from './components/Suppliers/SupplierList';
import SupplierForm from './components/Suppliers/SupplierForm';

// Inventory
import InventoryList from './components/Inventory/InventoryList';
import InventoryForm from './components/Inventory/InventoryForm';

// Categories
import CategoryList from './components/Categories/CategoryList';
import CategoryForm from './components/Categories/CategoryForm';

// Purchases
import PurchaseList from './components/Purchases/PurchaseList';
import PurchaseForm from './components/Purchases/PurchaseForm';

// Customers
import CustomerList from './components/Customers/CustomerList';
import CustomerForm from './components/Customers/CustomerForm';

// Sales Orders
import SalesOrderList from './components/SalesOrders/SalesOrderList';
import SalesOrderForm from './components/SalesOrders/SalesOrderForm';

// Measurements
import MeasurementList from './components/Measurements/MeasurementList';
import Settings from './components/Settings/Settings';
// import InvoicePrinterSettings from './components/Settings/InvoicePrinterSettings';
import MeasurementForm from './components/Measurements/MeasurementForm';

// Deliveries
import DeliveryList from './components/Deliveries/DeliveryList';
import DeliveryCalendar from './components/Deliveries/DeliveryCalendar';

// Audit Logs
import AuditLogList from './components/AuditLogs/AuditLogList';

// Fees
import FeeList from './components/Fees/FeeList';
import FeeForm from './components/Fees/FeeForm';

// VAT
import VatSettingForm from './components/Vat/VatSettingForm';

// Discounts
import DiscountList from './components/Discounts/DiscountList';
import DiscountForm from './components/Discounts/DiscountForm';

// Workers
import WorkerList from './components/Workers/WorkerList';
import WorkerForm from './components/Workers/WorkerForm';
import WorkerDetail from './components/Workers/WorkerDetail';

// Lab
import LabList from './components/Lab/LabList';
import LabForm from './components/Lab/LabForm';
import LabTaskHistory from './components/Lab/LabTaskHistory';
import LabWorkerDashboard from './components/Lab/LabWorkerDashboard';

// Fabric Sewing Types
import FabricSewingTypeList from './components/FabricSewingType/FabricSewingTypeList';
import FabricSewingTypeForm from './components/FabricSewingType/FabricSewingTypeForm';

// Accounting
import AccountingDashboard from './components/Accounting/AccountingDashboard';
import SalesReports from './components/Accounting/SalesReports';
import VATReports from './components/Accounting/VATReports';
import PaymentTracking from './components/Accounting/PaymentTracking';
import FinancialSummary from './components/Accounting/FinancialSummary';
import ReturnsReport from './components/Accounting/ReturnsReport';

// Returns
import ReturnsList from './components/Returns/ReturnsList';
import ReturnForm from './components/Returns/ReturnForm';

import Invoice  from './components/SalesOrders/Invoice';  

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />}
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Settings - Admin, Lab */}
        <Route path="settings" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <Settings />
          </ProtectedRoute>
        } />

        {/* Invoice Printer Settings - Admin, Sales */}
        {/* <Route path="invoice-printer-settings" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <InvoicePrinterSettings />
          </ProtectedRoute>
        } /> */}

        {/* User Management - Admin only */}
        <Route path="users" element={
          <ProtectedRoute roles={['Admin']}>
            <UserList />
          </ProtectedRoute>
        } />
        <Route path="users/new" element={
          <ProtectedRoute roles={['Admin']}>
            <UserForm />
          </ProtectedRoute>
        } />
        <Route path="users/:id" element={
          <ProtectedRoute roles={['Admin']}>
            <UserForm />
          </ProtectedRoute>
        } />

        {/* Suppliers - Admin, Purchasing */}
        <Route path="suppliers" element={
          <ProtectedRoute roles={['Admin', 'Purchasing']}>
            <SupplierList />
          </ProtectedRoute>
        } />
        <Route path="suppliers/new" element={
          <ProtectedRoute roles={['Admin', 'Purchasing']}>
            <SupplierForm />
          </ProtectedRoute>
        } />
        <Route path="suppliers/:id" element={
          <ProtectedRoute roles={['Admin', 'Purchasing']}>
            <SupplierForm />
          </ProtectedRoute>
        } />

                {/* Inventory - Admin, Inventory, Purchasing (view list) */}

        <Route path="inventory" element={
          <ProtectedRoute roles={['Admin', 'Inventory', 'Purchasing']}>
            <InventoryList />
          </ProtectedRoute>
        } />
         {/* Inventory - Sales (read-only access for API calls in forms/dropdowns) */}
        {/* Note: Sales cannot access the inventory page via menu, but can query the API */}
        <Route path="inventory/new" element={
          <ProtectedRoute roles={['Admin', 'Inventory']}>
            <InventoryForm />
          </ProtectedRoute>
        } />
        <Route path="inventory/:id" element={
          <ProtectedRoute roles={['Admin', 'Inventory']}>
            <InventoryForm />
          </ProtectedRoute>
        } />

        {/* Categories - Admin, Inventory, Purchasing */}
        <Route path="categories" element={
          <ProtectedRoute roles={['Admin', 'Inventory', 'Purchasing']}>
            <CategoryList />
          </ProtectedRoute>
        } />
        <Route path="categories/new" element={
          <ProtectedRoute roles={['Admin', 'Inventory', 'Purchasing']}>
            <CategoryForm />
          </ProtectedRoute>
        } />
        <Route path="categories/:id" element={
          <ProtectedRoute roles={['Admin', 'Inventory', 'Purchasing']}>
            <CategoryForm />
          </ProtectedRoute>
        } />

        {/* Purchases - Admin, Purchasing */}
        <Route path="purchases" element={
          <ProtectedRoute roles={['Admin', 'Purchasing']}>
            <PurchaseList />
          </ProtectedRoute>
        } />
        <Route path="purchases/new" element={
          <ProtectedRoute roles={['Admin', 'Purchasing']}>
            <PurchaseForm />
          </ProtectedRoute>
        } />
        <Route path="purchases/:id" element={
          <ProtectedRoute roles={['Admin', 'Purchasing']}>
            <PurchaseForm />
          </ProtectedRoute>
        } />

        {/* Customers - Admin, Sales */}
        <Route path="customers" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <CustomerList />
          </ProtectedRoute>
        } />
        <Route path="customers/new" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <CustomerForm />
          </ProtectedRoute>
        } />
        <Route path="customers/:id" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <CustomerForm />
          </ProtectedRoute>
        } />

        {/* Sales Orders - Admin, Sales */}
        <Route path="sales-orders" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <SalesOrderList />
          </ProtectedRoute>
        } />
        <Route path="sales-orders/new" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <SalesOrderForm />
          </ProtectedRoute>
        } />
        <Route path="sales-orders/:id" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <SalesOrderForm />
          </ProtectedRoute>
        } />

        {/* Measurements - Admin, Measurements, Sales */}
        <Route path="measurements" element={
          <ProtectedRoute roles={['Admin', 'Measurements', 'Sales']}>
            <MeasurementList />
          </ProtectedRoute>
        } />
        <Route path="measurements/new" element={
          <ProtectedRoute roles={['Admin', 'Measurements', 'Sales']}>
            <MeasurementForm />
          </ProtectedRoute>
        } />
        <Route path="measurements/:id" element={
          <ProtectedRoute roles={['Admin', 'Measurements', 'Sales']}>
            <MeasurementForm />
          </ProtectedRoute>
        } />

        {/* Deliveries - Admin, Sales */}
        <Route path="deliveries" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <DeliveryList />
          </ProtectedRoute>
        } />
        <Route path="deliveries/calendar" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <DeliveryCalendar />
          </ProtectedRoute>
        } />

        {/* Fees - Admin, Sales */}
        <Route path="fees" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <FeeList />
          </ProtectedRoute>
        } />
        <Route path="fees/new" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <FeeForm />
          </ProtectedRoute>
        } />
        <Route path="fees/:id" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <FeeForm />
          </ProtectedRoute>
        } />

        {/* VAT - Admin, Sales */}
        <Route path="vat" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <VatSettingForm />
          </ProtectedRoute>
        } />

        {/* Discounts - Admin, Sales */}
        <Route path="discounts" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <DiscountList />
          </ProtectedRoute>
        } />
        <Route path="discounts/new" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <DiscountForm />
          </ProtectedRoute>
        } />
        <Route path="discounts/:id" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <DiscountForm />
          </ProtectedRoute>
        } />

        {/* Audit Logs - Admin, Accounting */}
        <Route path="audit-logs" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <AuditLogList />
          </ProtectedRoute>
        } />

        {/* Workers - Admin, Lab */}
        <Route path="workers" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <WorkerList />
          </ProtectedRoute>
        } />
        <Route path="workers/new" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <WorkerForm />
          </ProtectedRoute>
        } />
        <Route path="workers/:id" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <WorkerDetail />
          </ProtectedRoute>
        } />
        <Route path="workers/:id/edit" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <WorkerForm />
          </ProtectedRoute>
        } />

        {/* Lab - Admin, Lab */}
        <Route path="lab" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <LabList />
          </ProtectedRoute>
        } />
        <Route path="lab/dashboard" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <LabWorkerDashboard />
          </ProtectedRoute>
        } />
        <Route path="lab/new" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <LabForm />
          </ProtectedRoute>
        } />
        <Route path="lab/:id" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <LabForm />
          </ProtectedRoute>
        } />
        <Route path="lab/:id/history" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <LabTaskHistory />
          </ProtectedRoute>
        } />

        {/* Fabric Sewing Types - Admin, Lab */}
        <Route path="fabric-sewing-types" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <FabricSewingTypeList />
          </ProtectedRoute>
        } />
        <Route path="fabric-sewing-types/new" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <FabricSewingTypeForm />
          </ProtectedRoute>
        } />
        <Route path="fabric-sewing-types/:id/edit" element={
          <ProtectedRoute roles={['Admin', 'Lab']}>
            <FabricSewingTypeForm />
          </ProtectedRoute>
        } />

        {/* Accounting - Admin, Accounting */}
        <Route path="accounting" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <AccountingDashboard />
          </ProtectedRoute>
        } />
        <Route path="accounting/sales-report" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <SalesReports />
          </ProtectedRoute>
        } />
        <Route path="accounting/vat-report" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <VATReports />
          </ProtectedRoute>
        } />
        <Route path="accounting/payment-tracking" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <PaymentTracking />
          </ProtectedRoute>
        } />
        <Route path="accounting/financial-summary" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <FinancialSummary />
          </ProtectedRoute>
        } />
        <Route path="accounting/returns-report" element={
          <ProtectedRoute roles={['Admin', 'Accounting']}>
            <ReturnsReport />
          </ProtectedRoute>
        } />

        {/* Returns - Admin, Sales */}
        <Route path="returns" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <ReturnsList />
          </ProtectedRoute>
        } />
        <Route path="returns/:id" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <ReturnForm />
          </ProtectedRoute>
        } />
        <Route path="returns/create/:salesOrderId" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <ReturnForm />
          </ProtectedRoute>
        } />

        {/* Invoice - Admin, Sales */}
        <Route path="invoice/:id" element={
          <ProtectedRoute roles={['Admin', 'Sales']}>
            <Invoice />
          </ProtectedRoute>
        } />
      </Route>

    </Routes>
  );
}

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <Router>
          <Toast />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
